export type FinnishCurrencyOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  showCurrency?: boolean;
  currency?: string; // defaults to EUR
};

export type FinnishNumberOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export type FinnishPercentageOptions = FinnishNumberOptions & {
  showSymbol?: boolean;
};

export type FinnishCoordinateOptions = FinnishNumberOptions;

function parseNumberish(value: number | string): number {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s|\u00A0/g, '').replace(/,/g, '.');
    return parseFloat(normalized);
  }

  return value;
}

export function formatFinnishNumber(
  amount: number | string,
  options: FinnishNumberOptions = {}
): string {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = options;

  const numericAmount = parseNumberish(amount);

  const formatter = new Intl.NumberFormat('fi-FI', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (Number.isNaN(numericAmount)) {
    return formatter.format(0);
  }

  return formatter.format(numericAmount);
}

export function formatFinnishPercentage(
  amount: number | string,
  options: FinnishPercentageOptions = {}
): string {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    showSymbol = true,
  } = options;

  const formatted = formatFinnishNumber(amount, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol ? `${formatted} %` : formatted;
}

export function formatFinnishCoordinate(
  amount: number | string,
  options: FinnishCoordinateOptions = {}
): string {
  const { minimumFractionDigits = 0, maximumFractionDigits = 14 } = options;

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    useGrouping: false,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  const numericAmount = parseNumberish(amount);

  if (Number.isNaN(numericAmount)) {
    return formatter.format(0);
  }

  return formatter.format(numericAmount);
}

export function formatFinnishCurrency(
  amount: number | string,
  options: FinnishCurrencyOptions = {}
): string {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showCurrency = true,
    currency = 'EUR',
  } = options;

  const numericAmount = parseNumberish(amount);

  if (Number.isNaN(numericAmount)) {
    return showCurrency ? '0,00 €' : '0,00';
  }

  const formatter = new Intl.NumberFormat('fi-FI', {
    style: showCurrency ? 'currency' : 'decimal',
    currency: (currency || 'EUR').toUpperCase(),
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return formatter.format(numericAmount);
}
